import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { XMarkIcon, ChevronDownIcon, ArrowTopRightOnSquareIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { submitJob } from '../services/api'
import type { LearnFromSource } from '../types/job'
import { errorText } from '../utils/errorText'
import PackagingEntitlementNotice, { EntitlementRefusalAction, isEntitlementRefusal } from './PackagingEntitlementNotice'

// URLs for browsing packages
const BROWSE_URLS: Record<LearnFromSource, string> = {
  winget: 'https://winstall.app',
  choco: 'https://community.chocolatey.org/packages',
}

/**
 * Parse clipboard content to extract package ID from winget or choco commands
 * Supports formats like:
 * - winget install Google.Chrome
 * - winget install --id Google.Chrome
 * - winget install -e --id Google.Chrome
 * - choco install firefox
 * - choco install firefox -y
 */
function parseClipboardCommand(text: string): { source: LearnFromSource; packageId: string } | null {
  const trimmed = text.trim()

  // WinGet patterns
  // Match: winget install [options] <package-id> or winget install --id <package-id>
  const wingetIdMatch = trimmed.match(/winget\s+install\s+(?:.*?\s+)?(?:--id|-i)\s+([^\s]+)/i)
  if (wingetIdMatch) {
    return { source: 'winget', packageId: wingetIdMatch[1] }
  }

  // WinGet without --id flag: winget install <package-id>
  const wingetSimpleMatch = trimmed.match(/winget\s+install\s+(?:-[ehs]\s+)*([A-Za-z0-9._-]+(?:\.[A-Za-z0-9._-]+)+)/i)
  if (wingetSimpleMatch) {
    return { source: 'winget', packageId: wingetSimpleMatch[1] }
  }

  // Chocolatey patterns
  // Match: choco install <package-id> [options]
  const chocoMatch = trimmed.match(/choco(?:latey)?\s+install\s+([^\s]+)/i)
  if (chocoMatch) {
    // Remove any trailing flags like -y
    const packageId = chocoMatch[1].replace(/^-.*/, '')
    if (packageId) {
      return { source: 'choco', packageId }
    }
  }

  return null
}

interface AddPackageModalProps {
  isOpen: boolean
  onClose: () => void
  initialSource?: LearnFromSource
  lockSource?: boolean  // If true, the source selector is disabled
}

interface PackageSource {
  id: LearnFromSource
  name: string
  description: string
  placeholder: string
  helpUrl: string
  helpText: string
  color: string
  icon: React.ReactNode
}

const PACKAGE_SOURCES: PackageSource[] = [
  {
    id: 'winget',
    name: 'WinGet',
    description: 'Windows Package Manager',
    placeholder: 'e.g., Google.Chrome, 7zip.7zip, Mozilla.Firefox',
    helpUrl: 'https://winget.run',
    helpText: 'winget.run',
    color: 'blue',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
      </svg>
    ),
  },
  {
    id: 'choco',
    name: 'Chocolatey',
    description: 'The Package Manager for Windows',
    placeholder: 'e.g., adobereader, googlechrome, firefox',
    helpUrl: 'https://community.chocolatey.org/packages',
    helpText: 'chocolatey.org/packages',
    color: 'amber',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm5.568 16.5c-.393.75-1.092 1.283-1.968 1.5-.516.128-1.068.128-1.584 0-.876-.217-1.575-.75-1.968-1.5l-.048-.09-.048.09c-.393.75-1.092 1.283-1.968 1.5-.516.128-1.068.128-1.584 0-.876-.217-1.575-.75-1.968-1.5C6.144 16.044 6 15.528 6 15c0-1.068.576-2.052 1.5-2.58.324-.186.684-.312 1.068-.372.42-.066.852-.066 1.272 0 .384.06.744.186 1.068.372.168.096.33.204.48.324l.612.504.612-.504c.15-.12.312-.228.48-.324.324-.186.684-.312 1.068-.372.42-.066.852-.066 1.272 0 .384.06.744.186 1.068.372.924.528 1.5 1.512 1.5 2.58 0 .528-.144 1.044-.432 1.5z"/>
      </svg>
    ),
  },
]

export default function AddPackageModal({ isOpen, onClose, initialSource = 'winget', lockSource = false }: AddPackageModalProps) {
  const navigate = useNavigate()
  const [selectedSource, setSelectedSource] = useState<LearnFromSource>(initialSource)

  // Sync selectedSource with initialSource when modal opens or initialSource changes
  useEffect(() => {
    if (isOpen) {
      setSelectedSource(initialSource)
    }
  }, [isOpen, initialSource])
  const [packageId, setPackageId] = useState('')
  const [version, setVersion] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  // AI analysis is always enabled for winget/choco sources
  const enableAgentic = true
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ jobId: string; appId: string } | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [clipboardMessage, setClipboardMessage] = useState<string | null>(null)

  const currentSource = PACKAGE_SOURCES.find(s => s.id === selectedSource) || PACKAGE_SOURCES[0]

  // Reset on every close path (X, backdrop, View Catalog, View Job Status) so reopening shows
  // the form for the next package rather than the previous submission.
  useEffect(() => {
    if (!isOpen) {
      setError(null)
      setSuccess(null)
      setPackageId('')
      setVersion('')
      setShowAdvanced(false)
      setDropdownOpen(false)
      setClipboardMessage(null)
    }
  }, [isOpen])

  // Handle clipboard parsing when window regains focus
  const handleClipboardParse = useCallback(async () => {
    if (!isOpen || success) return

    try {
      // Check if clipboard API is available
      if (!navigator.clipboard?.readText) {
        return
      }

      const text = await navigator.clipboard.readText()
      if (!text) return

      const parsed = parseClipboardCommand(text)
      if (parsed) {
        // Auto-fill the package ID and switch source if needed
        if (!lockSource && parsed.source !== selectedSource) {
          setSelectedSource(parsed.source)
        }
        setPackageId(parsed.packageId)
        setError(null)

        // Show a brief message that we detected a command
        const sourceName = PACKAGE_SOURCES.find(s => s.id === parsed.source)?.name || parsed.source
        setClipboardMessage(`Detected ${sourceName} command: ${parsed.packageId}`)
        setTimeout(() => setClipboardMessage(null), 3000)
      }
    } catch (err) {
      // Clipboard access denied or other error - silently ignore
      console.debug('Clipboard access denied or unavailable')
    }
  }, [isOpen, success, lockSource, selectedSource])

  // Parse clipboard when modal opens
  useEffect(() => {
    if (isOpen && !success) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        handleClipboardParse()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen]) // Only trigger on modal open, not on every dependency change

  // Listen for window focus to parse clipboard
  useEffect(() => {
    if (!isOpen) return

    const handleFocus = () => {
      handleClipboardParse()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [isOpen, handleClipboardParse])

  // Open browse URL in new tab
  const handleBrowse = useCallback((source: LearnFromSource) => {
    window.open(BROWSE_URLS[source], '_blank', 'noopener,noreferrer')
  }, [])

  // Manual paste from clipboard button
  const handlePasteFromClipboard = useCallback(async () => {
    await handleClipboardParse()
  }, [handleClipboardParse])

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (!packageId.trim()) {
      setError(`Please enter a ${currentSource.name} Package ID`)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Always include both psadt and intune
      const deploymentMethods: ('psadt' | 'intune')[] = ['psadt', 'intune']

      // Map selectedSource to source_type
      // 'winget' -> 'winget', 'choco' -> 'chocolatey'
      const sourceType = selectedSource === 'choco' ? 'chocolatey' : 'winget'

      const result = await submitJob({
        source_type: sourceType,
        source_reference: packageId.trim(),
        deployment_methods: deploymentMethods,
        enable_agentic: enableAgentic,
        learn_from: selectedSource,
        ...(version.trim() && { version: version.trim() }),
      })

      if (result.duplicate) {
        setError(`This package is already being processed. Job ID: ${result.existing_job_id}`)
      } else {
        setSuccess({ jobId: result.job_id, appId: result.app_id || '' })
      }
    } catch (err: any) {
      // Handle 409 Conflict (duplicate)
      if (err.response?.status === 409) {
        const existingJobId = err.response.headers['x-duplicate-job-id']
        setError(`This package is already being processed.${existingJobId ? ` Job ID: ${existingJobId}` : ''}`)
      } else {
        setError(errorText(err, 'Failed to submit packaging job'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleViewJob = () => {
    if (success?.jobId) {
      navigate('/settings/jobs', { state: { highlightJobId: success.jobId } })
    }
    onClose()
  }

  const handleClose = () => {
    onClose()
  }

  const handleViewApp = () => {
    // Navigate to app catalog (pending app will show there)
    onClose()
  }

  const handleSourceSelect = (sourceId: LearnFromSource) => {
    setSelectedSource(sourceId)
    setDropdownOpen(false)
    setPackageId('')
    setError(null)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md bg-theme-card rounded-lg shadow-xl border border-theme-border-primary">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
            <h2 className="text-lg font-semibold text-theme-text-primary">
              {success ? 'Job Submitted' : 'Add Package'}
            </h2>
            <button
              onClick={handleClose}
              className="text-theme-text-tertiary hover:text-theme-text-primary transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              // Success state
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-theme-text-primary mb-2">Package Added to Catalog</h3>
                <p className="text-theme-text-secondary mb-4">
                  The app has been added to your catalog with a <span className="text-yellow-600 dark:text-yellow-400">Pending</span> status.
                  Packaging is now in progress.
                </p>
                <div className="bg-theme-tertiary rounded-lg p-3 mb-4 space-y-2">
                  <div>
                    <p className="text-xs text-theme-text-tertiary">Source</p>
                    <p className="text-theme-text-primary font-medium text-sm">{currentSource.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-theme-text-tertiary">Package ID</p>
                    <p className="text-theme-text-primary font-mono text-sm">{packageId}</p>
                  </div>
                  <div>
                    <p className="text-xs text-theme-text-tertiary">Job ID</p>
                    <p className="text-theme-text-primary font-mono text-sm truncate">{success.jobId}</p>
                  </div>
                </div>
                <p className="text-sm text-theme-text-tertiary mb-4">
                  This process typically takes 5-15 minutes. The app status will update to <span className="text-green-600 dark:text-green-400">Ready</span> once complete.
                </p>
              </div>
            ) : (
              // Form state
              <>
                {/* Source Selector - Locked display or Dropdown */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Package Source
                  </label>
                  {lockSource ? (
                    // Locked: show as static display
                    <div className="w-full flex items-center px-3 py-2.5 bg-theme-tertiary border border-theme-border-primary rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`${currentSource.color === 'blue' ? 'text-blue-500' : 'text-amber-500'}`}>
                          {currentSource.icon}
                        </span>
                        <div className="text-left">
                          <span className="font-medium text-theme-text-primary">{currentSource.name}</span>
                          <span className="text-theme-text-tertiary text-sm ml-2">
                            {currentSource.description}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Unlocked: show dropdown selector
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary hover:border-theme-border-secondary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className={`${currentSource.color === 'blue' ? 'text-blue-500' : 'text-amber-500'}`}>
                            {currentSource.icon}
                          </span>
                          <div className="text-left">
                            <span className="font-medium">{currentSource.name}</span>
                            <span className="text-theme-text-tertiary text-sm ml-2">
                              {currentSource.description}
                            </span>
                          </div>
                        </div>
                        <ChevronDownIcon className={`w-5 h-5 text-theme-text-tertiary transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Menu */}
                      {dropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-theme-card border border-theme-border-primary rounded-lg shadow-lg overflow-hidden">
                          {PACKAGE_SOURCES.map((source) => (
                            <button
                              key={source.id}
                              type="button"
                              onClick={() => handleSourceSelect(source.id)}
                              className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-theme-secondary transition-colors ${
                                selectedSource === source.id ? 'bg-theme-secondary' : ''
                              }`}
                            >
                              <span className={`${source.color === 'blue' ? 'text-blue-500' : 'text-amber-500'}`}>
                                {source.icon}
                              </span>
                              <div>
                                <div className="font-medium text-theme-text-primary">{source.name}</div>
                                <div className="text-sm text-theme-text-tertiary">{source.description}</div>
                              </div>
                              {selectedSource === source.id && (
                                <svg className="w-5 h-5 text-blue-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Package ID Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    {currentSource.name} Package ID
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={packageId}
                      onChange={(e) => setPackageId(e.target.value)}
                      placeholder={currentSource.placeholder}
                      className="flex-1 px-3 py-2.5 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handlePasteFromClipboard}
                      title="Paste from clipboard"
                      className="px-3 py-2.5 bg-theme-tertiary border border-theme-border-primary rounded-lg text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-secondary transition-colors"
                    >
                      <ClipboardDocumentIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Clipboard detection message */}
                  {clipboardMessage && (
                    <div className="mt-2 flex items-center gap-2 text-green-600 dark:text-green-400 text-xs">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {clipboardMessage}
                    </div>
                  )}

                  {/* Browse button - shows only the selected source */}
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => handleBrowse(selectedSource)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        selectedSource === 'winget'
                          ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                          : 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                      }`}
                    >
                      <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                      Browse {currentSource.name}
                    </button>
                  </div>

                  <p className="mt-3 text-xs text-theme-text-tertiary">
                    Click browse to find a package, copy the install command, and return here.
                    We'll automatically detect the package ID from your clipboard.
                  </p>
                </div>

                {/* Package Info */}
                <div className="mb-6 p-3 bg-theme-tertiary rounded-lg">
                  <p className="text-xs text-theme-text-secondary">
                    <span className="font-medium text-theme-text-primary">Included:</span> PSADT Package + Intune Package (.intunewin)
                  </p>
                </div>

                {/* Advanced Options */}
                <div className="mb-6">
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
                  >
                    <svg
                      className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Advanced Options
                  </button>

                  {showAdvanced && (
                    <div className="mt-3 p-4 bg-theme-tertiary rounded-lg border border-theme-border-primary space-y-4">
                      {/* Version Input */}
                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Version (optional)
                        </label>
                        <input
                          type="text"
                          value={version}
                          onChange={(e) => setVersion(e.target.value)}
                          placeholder="Leave empty for latest version"
                          className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="mt-2 text-xs text-theme-text-tertiary">
                          Specify a version like "7.86.0" to pin to that exact version.
                          Leave empty to always use the latest available version.
                        </p>
                      </div>

                      {/* Testing is always enabled: every package runs an
                          install/detect/uninstall sim-test before it can be used. */}
                      <div className="flex items-start gap-3">
                        <div>
                          <p className="text-sm font-medium text-theme-text-secondary">
                            Install/uninstall testing: always on
                          </p>
                          <p className="mt-1 text-xs text-theme-text-tertiary">
                            Every package is validated with an automated install → detect → uninstall
                            test on a clean VM before it's made available. This adds a few minutes but
                            ensures broken packages never reach your endpoints.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Error */}
                <PackagingEntitlementNotice />
                {error && (
                  <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/50 border border-red-300 dark:border-red-700 rounded-lg">
                    <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
                    {isEntitlementRefusal(error) && <EntitlementRefusalAction />}
                  </div>
                )}

                {/* Info */}
                <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-blue-700 dark:text-blue-300 text-sm">
                    The packaging process will fetch package information from {currentSource.name},
                    analyze it, and create deployment-ready packages.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t border-theme-border-primary">
            {success ? (
              <>
                <button
                  onClick={handleViewJob}
                  className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary transition-colors"
                >
                  View Job Status
                </button>
                <button
                  onClick={handleViewApp}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  View Catalog
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !packageId.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    'Start Packaging'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
